import { useRef, useState } from "react";
import s from "./StackedBarChart.module.css";

export interface ChartSegment {
  key: string;
  label: string;
  value: number;
  /** CSS color — always a design token, e.g. "var(--op-warn)". */
  color: string;
}
export interface ChartRow {
  key: string;
  /** Left row label; null collapses the label column (single-bar charts). */
  label: string | null;
  segments: ChartSegment[];
  /** Muted annotation after the tip value (e.g. an accuracy %). */
  note?: string;
}

function rowTotal(row: ChartRow): number {
  return row.segments.reduce((acc, seg) => acc + seg.value, 0);
}

function pctWidth(value: number, base: number): string {
  return `${+((value / base) * 100).toFixed(3)}%`;
}

/** Horizontal stacked-bar chart (ADR 152) on the shared design tokens.
 *  Marks follow the dataviz specs: ≤24px bars with a 2px surface gap between
 *  segments, 4px rounded data-end (square baseline), no borders. Identity is
 *  never color-alone: an auto-derived legend (≥2 series), a full readout in
 *  each track's aria-label, and a per-row tooltip on hover AND keyboard focus.
 *  Values stay reachable without hovering — callers keep a table/KPI twin. */
export function StackedBarChart({ rows, normalized = false, format = String, totalLabel }: {
  rows: ChartRow[];
  /** true → each row scales to its own total (part-to-whole); false → all
   *  rows share one linear scale (concentration comparison). */
  normalized?: boolean;
  format?: (n: number) => string;
  /** Names the number printed after each bar. That figure is the row's TOTAL,
   *  but nothing on screen said so — sitting beside a legend whose first entry
   *  was "Divergência de quantidade", a bare 8 read as 8 quantity divergences
   *  when the real count was 0 (ADR 254). Given a label, the tooltip spells it
   *  out where the reader is already looking. */
  totalLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ row: ChartRow; x: number; y: number } | null>(null);

  const maxTotal = Math.max(1, ...rows.map(rowTotal));

  // Legend derives from the declared segments (zero values included — the
  // class exists even when this slice of data has none), first-seen order.
  const legend: ChartSegment[] = [];
  for (const row of rows) {
    for (const seg of row.segments) {
      if (!legend.some((l) => l.key === seg.key)) legend.push(seg);
    }
  }

  const aria = (row: ChartRow) => {
    const parts = row.segments.filter((x) => x.value > 0)
      .map((x) => `${x.label} ${format(x.value)}`).join(", ");
    return row.label == null ? parts : `${row.label}: ${parts}`;
  };

  const showTip = (row: ChartRow, el: HTMLElement, clientX: number | null) => {
    const root = rootRef.current;
    if (!root) return setTip({ row, x: 0, y: 0 });
    const rootBox = root.getBoundingClientRect();
    const elBox = el.getBoundingClientRect();
    const x = clientX == null ? elBox.left - rootBox.left : clientX - rootBox.left;
    setTip({ row, x: Math.max(0, x), y: elBox.top - rootBox.top });
  };

  return (
    <div ref={rootRef} className={s.chart}>
      {rows.map((row) => {
        const total = rowTotal(row);
        const base = normalized ? Math.max(1, total) : maxTotal;
        return (
          <div key={row.key} className={row.label == null ? `${s.row} ${s.bare}` : s.row}>
            {row.label != null && <div className={s.label}>{row.label}</div>}
            <div
              data-chart-track
              tabIndex={0}
              role="img"
              aria-label={aria(row)}
              className={s.track}
              onPointerEnter={(e) => showTip(row, e.currentTarget, e.clientX)}
              onPointerMove={(e) => showTip(row, e.currentTarget, e.clientX)}
              onPointerLeave={() => setTip(null)}
              onFocus={(e) => showTip(row, e.currentTarget, null)}
              onBlur={() => setTip(null)}
            >
              {row.segments.filter((seg) => seg.value > 0).map((seg) => (
                <div
                  data-chart-seg
                  key={seg.key}
                  className={s.seg}
                  style={{ width: pctWidth(seg.value, base), backgroundColor: seg.color }}
                />
              ))}
            </div>
            <div className={s.tipCell}>
              <span className={s.value}>{format(total)}</span>
              {row.note && <span className={s.note}>{row.note}</span>}
            </div>
          </div>
        );
      })}

      {legend.length >= 2 && (
        <ul data-chart-legend className={s.legend}>
          {legend.map((l) => (
            <li key={l.key} className={s.legendItem}>
              <span className={s.swatch} style={{ backgroundColor: l.color }} aria-hidden="true" />
              {l.label}
            </li>
          ))}
        </ul>
      )}

      {tip && (
        <div
          data-testid="chart-tooltip"
          aria-hidden="true"
          className={s.tooltip}
          style={{ left: tip.x, top: tip.y }}
        >
          {tip.row.label != null && <div className={s.tooltipTitle}>{tip.row.label}</div>}
          {tip.row.segments.map((seg) => (
            <div key={seg.key} className={s.tooltipRow}>
              <span className={s.lineKey} style={{ backgroundColor: seg.color }} aria-hidden="true" />
              <b className={s.tooltipValue}>{format(seg.value)}</b>
              <span className={s.tooltipLabel}>{seg.label}</span>
            </div>
          ))}
          {totalLabel && (
            <div className={`${s.tooltipRow} ${s.tooltipTotal}`}>
              <span className={s.lineKey} aria-hidden="true" />
              <b className={s.tooltipValue}>{format(rowTotal(tip.row))}</b>
              <span className={s.tooltipLabel}>{totalLabel}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
